/**
 * Source: https://github.com/rrudol/useCookie/blob/master/src/index.js
 * License: MIT
 */
import { ChangeEvent, useLayoutEffect, useState } from 'react'
import Cookies from 'js-cookie'

/**
 * useCookie - React Hook for Cookies based on js-cookie
 * @param {string} key Cookie name
 * @param {Object|string} [initialValue]  Value will be assigned if cookie doesn't exist.
 * @returns {Array} Returns cookie value, and the function to update it.
 */
export function useCookie(
  key: string,
  initialValue: string | undefined,
  updateOnChange: boolean = false
): [string | undefined, (value: string, options: Cookies.CookieAttributes) => void, () => void] {
  const [item, setInnerValue] = useState(() => {
    return Cookies.get(key) || initialValue
  })

  /**
   * Set value of cookie
   * @param {string} value - must JSON.stringify objects if needed
   * @param {Cookies.CookieAttributes} [options]
   */
  const setValue = (value: string, options: Cookies.CookieAttributes) => {
    setInnerValue(value)
    Cookies.set(key, value, options)
  }

  const removeValue = () => {
    setInnerValue(undefined)
    Cookies.remove(key)
  }

  useLayoutEffect(() => {
    if (!updateOnChange) return
    let onChange: (event: ChangeEvent) => void
    if (typeof window !== 'undefined') {
      onChange = (event: ChangeEvent) => {
        // @ts-expect-error event type
        if (event.changed) {
          // @ts-expect-error event type
          for (const { name } of event.changed) {
            if (name === key) {
              setInnerValue(Cookies.get(key))
            }
          }
        }
      }
      ;(window as any).cookieStore?.addEventListener('change', onChange)
    }

    return () => {
      if (typeof window !== 'undefined') {
        ;(window as any).cookieStore?.removeEventListener('change', onChange)
      }
    }
  }, [updateOnChange, key])

  return [item, setValue, removeValue]
}

export default useCookie
